import React, { useEffect, useState } from 'react';
import { Plus, Trash2, Save, Wand2 } from 'lucide-react';
import evalsService, { EvalTest, TEST_TYPES } from '../../services/evalsService';
import docshawkService, { SourceDocument } from '../../services/docshawkService';
import { PageHeader, Spinner, Button, IconButton } from '../../components/ui';
import '../../styles/theme.css';

const OPS = ['after', 'before', 'gt', 'lt', 'gte', 'lte'];
const blank = (): EvalTest => ({ key: '', name: '', type: 'present', field_key: '', params: {}, required: false });
const slug = (s: string) => s.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '');

const Evals: React.FC = () => {
  const [rows, setRows] = useState<EvalTest[]>([]);
  const [fields, setFields] = useState<string[]>([]);
  const [docs, setDocs] = useState<SourceDocument[]>([]);
  const [docId, setDocId] = useState('');
  const [wholeDoc, setWholeDoc] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [suggesting, setSuggesting] = useState(false);
  const [msg, setMsg] = useState('');

  useEffect(() => {
    (async () => {
      try {
        const [suite, schema, docList] = await Promise.all([
          evalsService.getSuite(),
          docshawkService.getActiveSchema(),
          docshawkService.listDocuments(1, 50).catch(() => ({ documents: [] as SourceDocument[] })),
        ]);
        setRows(suite.current_version?.tests?.length ? suite.current_version.tests.map((t) => ({ ...t })) : [blank()]);
        setFields((schema.current_version?.fields || []).map((f) => f.key));
        const dl = (docList as any).documents || [];
        setDocs(dl); if (dl[0]) setDocId(dl[0].id);
      } finally { setLoading(false); }
    })();
  }, []);

  const upd = (i: number, patch: Partial<EvalTest>) => setRows((r) => r.map((x, j) => (j === i ? { ...x, ...patch } : x)));
  const updParam = (i: number, k: string, v: any) => setRows((r) => r.map((x, j) => (j === i ? { ...x, params: { ...x.params, [k]: v } } : x)));

  const save = async () => {
    const tests = rows
      .map((r) => ({ ...r, key: r.key.trim() || slug(r.name || r.field_key || r.type) }))
      .filter((r) => r.key);
    if (!tests.length) { setMsg('Add at least one test.'); return; }
    setSaving(true); setMsg('');
    try {
      const res = await evalsService.setSuite(tests);
      setMsg(`Saved ${res.current_version.tests.length} tests (v${res.current_version.version_number}).`);
    } catch (e: any) { setMsg(e?.response?.data?.detail || 'Save failed'); }
    finally { setSaving(false); }
  };

  const suggest = async () => {
    if (!docId) { setMsg('Upload a document first to suggest tests.'); return; }
    setSuggesting(true); setMsg('');
    try {
      const res = await evalsService.suggest(docId, wholeDoc);
      const have = new Set(rows.filter((r) => r.key).map((r) => r.key));
      const additions = res.tests.filter((t) => !have.has(t.key));
      setRows((cur) => [...cur.filter((r) => r.key || r.name), ...additions]);
      setMsg(`Suggested ${res.tests.length} tests (from parameters + document). Review and save.`);
    } catch (e: any) { setMsg(e?.response?.data?.detail || 'Suggest failed'); }
    finally { setSuggesting(false); }
  };

  if (loading) return <div><PageHeader title="Evals" /><div className="mx-card mx-card-pad"><Spinner label="Loading…" /></div></div>;

  const paramsCell = (r: EvalTest, i: number) => {
    switch (r.type) {
      case 'regex':
        return <input className="mx-input mx-mono" style={cell} placeholder="regex" value={r.params.regex || ''}
          onChange={(e) => updParam(i, 'regex', e.target.value)} />;
      case 'range':
        return <div style={{ display: 'flex', gap: 4 }}>
          <input className="mx-input" style={{ ...cell, width: 70 }} placeholder="min" value={r.params.min ?? ''}
            onChange={(e) => updParam(i, 'min', e.target.value === '' ? undefined : Number(e.target.value))} />
          <input className="mx-input" style={{ ...cell, width: 70 }} placeholder="max" value={r.params.max ?? ''}
            onChange={(e) => updParam(i, 'max', e.target.value === '' ? undefined : Number(e.target.value))} />
        </div>;
      case 'cross_field':
        return <div style={{ display: 'flex', gap: 4 }}>
          <input className="mx-input mx-mono" style={{ ...cell, width: 120 }} list="mx-fields" placeholder="other field"
            value={r.params.other_field || ''} onChange={(e) => updParam(i, 'other_field', e.target.value)} />
          <select className="mx-select" style={{ ...cell, width: 90 }} value={r.params.op || 'after'}
            onChange={(e) => updParam(i, 'op', e.target.value)}>{OPS.map((o) => <option key={o}>{o}</option>)}</select>
        </div>;
      case 'llm_judge':
        return <input className="mx-input" style={cell} placeholder="judge prompt" value={r.params.judge_prompt || ''}
          onChange={(e) => updParam(i, 'judge_prompt', e.target.value)} />;
      default:
        return <span className="mx-subtle" style={{ fontSize: 12 }}>—</span>;
    }
  };

  return (
    <div>
      <PageHeader title="Evals"
        subtitle="Configure quality tests. A failing 'required' test blocks export until fixed or overridden. Same tests judge a fine-tuned model's promotion."
        actions={<>
          {docs.length > 0 && (
            <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
              <select className="mx-select" style={{ width: 'auto', maxWidth: 180 }} value={docId} onChange={(e) => setDocId(e.target.value)}>
                {docs.map((d) => <option key={d.id} value={d.id}>{d.filename}</option>)}
              </select>
              <Button icon={Wand2} variant="secondary" size="sm" onClick={suggest} disabled={suggesting}>
                {suggesting ? 'Thinking…' : 'Suggest tests'}</Button>
              <label className="mx-subtle" style={{ fontSize: 12, display: 'flex', alignItems: 'center', gap: 4 }}>
                <input type="checkbox" checked={wholeDoc} onChange={(e) => setWholeDoc(e.target.checked)} /> whole doc</label>
            </div>
          )}
          <Button icon={Save} variant="primary" size="sm" onClick={save} disabled={saving}>
            {saving ? 'Saving…' : 'Save tests'}</Button>
        </>} />

      {msg && <div className="mx-card mx-card-pad" style={{ marginBottom: 16, fontSize: 13 }}>{msg}</div>}
      <datalist id="mx-fields">{fields.map((f) => <option key={f} value={f} />)}</datalist>

      <div className="mx-card">
        <table className="mx-table">
          <thead><tr>
            <th>Name</th><th style={{ width: 130 }}>Type</th><th style={{ width: 150 }}>Field</th>
            <th>Params</th><th style={{ width: 70 }}>Required</th><th style={{ width: 50 }}></th>
          </tr></thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={i}>
                <td><input className="mx-input" style={cell} placeholder="Leverage ratio present" value={r.name}
                  onChange={(e) => upd(i, { name: e.target.value })} /></td>
                <td><select className="mx-select" style={cell} value={r.type} onChange={(e) => upd(i, { type: e.target.value, params: {} })}>
                  {TEST_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}</select></td>
                <td>{r.type === 'llm_judge' ? <span className="mx-subtle" style={{ fontSize: 12 }}>whole doc</span> : (
                  <input className="mx-input mx-mono" style={cell} list="mx-fields" placeholder="field key"
                    value={r.field_key || ''} onChange={(e) => upd(i, { field_key: e.target.value })} />)}</td>
                <td>{paramsCell(r, i)}</td>
                <td style={{ textAlign: 'center' }}><input type="checkbox" checked={r.required}
                  onChange={(e) => upd(i, { required: e.target.checked })} /></td>
                <td><IconButton icon={Trash2} label="Remove test" style={{ color: 'var(--mx-danger)' }}
                  onClick={() => setRows((rr) => rr.filter((_, j) => j !== i))} /></td>
              </tr>
            ))}
          </tbody>
        </table>
        <div className="mx-card-pad" style={{ borderTop: '1px solid var(--mx-border)' }}>
          <Button icon={Plus} variant="secondary" size="sm" onClick={() => setRows((r) => [...r, blank()])}>
            Add test</Button>
        </div>
      </div>
    </div>
  );
};

const cell: React.CSSProperties = { padding: '6px 8px', fontSize: 13 };

export default Evals;
